'use client';

import React, { useState, useRef } from 'react';
import Link from 'next/link';
import { NAMES_DATA } from '../../names-reference/data/namesData';
import type { NameEntry } from '../../names-reference/data/namesData';

export interface NameHighlightData {
  id: string;
  arabic: string;
  transliteration: string;
  meaning: string;
  root: string;
  rootMeanings: string[];
  relatedSequences: { id: string; title: string; city: string }[];
  irlTiles: { id: string; label: string }[];
  /** Optional: full page path for "explore further" */
  explorePath?: string;
  exploreLabel?: string;
  /** Optional: rich panel content */
  panelContent?: React.ReactNode;
}

// Name data registry for hover cards
export const NAME_HIGHLIGHT_DATA: Record<string, NameHighlightData> = {
  qadir: {
    id: 'qadir',
    arabic: 'الْقَدِير',
    transliteration: 'Al-Qadir',
    meaning: 'The All-Powerful',
    root: 'Qaf - Dal - Ra',
    rootMeanings: ['To measure something', 'To make manifest the measure', 'To distribute by measure', 'To have the power', 'To ordain, To decree'],
    relatedSequences: [{ id: 's1', title: 'Big Bluestem', city: 'Chicago' }, { id: 's3', title: 'Canopy Photosynthesis', city: 'Atlanta' }, { id: 's13', title: 'The Waiting Seed', city: 'All Cities' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }, { id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=qadir',
    exploreLabel: 'Names Reference — Al-Qadir',
  },
  badi: {
    id: 'badi',
    arabic: 'الْبَدِيع',
    transliteration: 'Al-Badi',
    meaning: 'The Originator',
    root: 'Ba - Dal - Ayn',
    rootMeanings: ['To originate without precedent', 'To create something new', 'To innovate'],
    relatedSequences: [{ id: 's13', title: 'The Waiting Seed', city: 'All Cities' }, { id: 's18', title: 'Clay', city: 'All Cities' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }],
    explorePath: '/names-reference?name=badi',
    exploreLabel: 'Names Reference — Al-Badi',
  },
  alim: {
    id: 'alim',
    arabic: 'الْعَلِيم',
    transliteration: 'Al-Alim',
    meaning: 'The All-Knowing',
    root: 'Ayn - Lam - Mim',
    rootMeanings: ['To have knowledge', 'To be cognizant', 'To be firmly rooted in knowledge', 'To know the minute details', 'To be wise and act accordingly'],
    relatedSequences: [{ id: 's4', title: 'Mycorrhizal Network', city: 'Atlanta' }, { id: 's6', title: 'Salmon Run', city: 'Portland' }],
    irlTiles: [{ id: 'community-garden', label: 'Community Garden' }, { id: 'fire-hydrant', label: 'Fire Hydrant' }],
    explorePath: '/names-reference?name=alim',
    exploreLabel: 'Names Reference — Al-Alim',
  },
  khabir: {
    id: 'khabir',
    arabic: 'الْخَبِير',
    transliteration: 'Al-Khabir',
    meaning: 'The All-Aware',
    root: 'Kha - Ba - Ra',
    rootMeanings: ['To be thoroughly informed', 'To have inner knowledge', 'To be aware of hidden matters', 'To know the real inner nature of something'],
    relatedSequences: [{ id: 's2', title: 'Wetland Filtration', city: 'Detroit' }, { id: 's7', title: 'Longleaf Pine', city: 'Durham' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }],
    explorePath: '/names-reference?name=khabir',
    exploreLabel: 'Names Reference — Al-Khabir',
  },
  basir: {
    id: 'basir',
    arabic: 'الْبَصِير',
    transliteration: 'Al-Basir',
    meaning: 'The All-Seeing',
    root: 'Ba - Sad - Ra',
    rootMeanings: ['To see', 'To perceive', 'To have insight', 'To be sharp-sighted'],
    relatedSequences: [{ id: 's3', title: 'Canopy Photosynthesis', city: 'Atlanta' }, { id: 's10', title: 'Monarch Migration', city: 'Chicago' }],
    irlTiles: [{ id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=basir',
    exploreLabel: 'Names Reference — Al-Basir',
  },
  nur: {
    id: 'nur',
    arabic: 'النُّور',
    transliteration: 'An-Nur',
    meaning: 'The Light',
    root: 'Nun - Waw - Ra',
    rootMeanings: ['Light', 'Illumination', 'To enlighten', 'To make visible'],
    relatedSequences: [{ id: 's3', title: 'Canopy Photosynthesis', city: 'Atlanta' }, { id: 's27', title: 'Electromagnetism', city: 'All Cities' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }, { id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=nur',
    exploreLabel: 'Names Reference — An-Nur',
  },
  khaliq: {
    id: 'khaliq',
    arabic: 'الْخَالِق',
    transliteration: 'Al-Khaliq',
    meaning: 'The Creator',
    root: 'Kha - Lam - Qaf',
    rootMeanings: ['To create', 'To bring into existence', 'To measure out accurately', 'To fashion according to a pattern'],
    relatedSequences: [{ id: 's1', title: 'Big Bluestem', city: 'Chicago' }, { id: 's5', title: 'Amphibian Metamorphosis', city: 'Detroit' }, { id: 's18', title: 'Clay', city: 'All Cities' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }, { id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=khaliq',
    exploreLabel: 'Names Reference — Al-Khaliq',
  },
  hakim: {
    id: 'hakim',
    arabic: 'الْحَكِيم',
    transliteration: 'Al-Hakim',
    meaning: 'The All-Wise',
    root: 'Ha - Kaf - Mim',
    rootMeanings: ['To prevent wrongdoing', 'To be wise', 'To know the true nature of things', 'To pass judgment'],
    relatedSequences: [{ id: 's4', title: 'Mycorrhizal Network', city: 'Atlanta' }, { id: 's8', title: 'River Bluff Geology', city: 'Cincinnati' }],
    irlTiles: [{ id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=hakim',
    exploreLabel: 'Names Reference — Al-Hakim',
  },
  latif: {
    id: 'latif',
    arabic: 'اللَّطِيف',
    transliteration: 'Al-Latif',
    meaning: 'The All-Subtle',
    root: 'Lam - Ta - Fa',
    rootMeanings: ['Subtle', 'Gentle', 'Kind', 'Imperceptible', 'Fine in perception'],
    relatedSequences: [{ id: 's4', title: 'Mycorrhizal Network', city: 'Atlanta' }, { id: 's17', title: 'Duckweed', city: 'Detroit' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }],
    explorePath: '/names-reference?name=latif',
    exploreLabel: 'Names Reference — Al-Latif',
  },
  muhyi: {
    id: 'muhyi',
    arabic: 'الْمُحْيِي',
    transliteration: 'Al-Muhyi',
    meaning: 'The Bestower of Life',
    root: 'Ha - Ya - Ya',
    rootMeanings: ['To give life', 'To revive', 'To animate', 'To restore'],
    relatedSequences: [{ id: 's5', title: 'Amphibian Metamorphosis', city: 'Detroit' }, { id: 's6', title: 'Salmon Run', city: 'Portland' }, { id: 's13', title: 'The Waiting Seed', city: 'All Cities' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }, { id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=muhyi',
    exploreLabel: 'Names Reference — Al-Muhyi',
  },
  mumit: {
    id: 'mumit',
    arabic: 'الْمُمِيت',
    transliteration: 'Al-Mumit',
    meaning: 'The Bringer of Death',
    root: 'Mim - Waw - Ta',
    rootMeanings: ['To cause death', 'To bring to an end', 'To complete a cycle'],
    relatedSequences: [{ id: 's6', title: 'Salmon Run', city: 'Portland' }, { id: 's5', title: 'Amphibian Metamorphosis', city: 'Detroit' }],
    irlTiles: [{ id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=mumit',
    exploreLabel: 'Names Reference — Al-Mumit',
  },
  rahman: {
    id: 'rahman',
    arabic: 'الرَّحْمَن',
    transliteration: 'Ar-Rahman',
    meaning: 'The Most Compassionate',
    root: 'Ra - Ha - Mim',
    rootMeanings: ['Tenderness', 'Gentleness', 'Compassion', 'Womb', 'To show love', 'All that benefits others'],
    relatedSequences: [{ id: 's1', title: 'Big Bluestem', city: 'Chicago' }, { id: 's3', title: 'Canopy Photosynthesis', city: 'Atlanta' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }, { id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=rahman',
    exploreLabel: 'Names Reference — Ar-Rahman',
  },
  raheem: {
    id: 'raheem',
    arabic: 'الرَّحِيم',
    transliteration: 'Ar-Raheem',
    meaning: 'The Most Kind',
    root: 'Ra - Ha - Mim',
    rootMeanings: ['Mercy', 'Compassion', 'Kindness', 'Ongoing mercy in action'],
    relatedSequences: [{ id: 's11', title: 'Sycamore Sentinel', city: 'Louisville' }],
    irlTiles: [{ id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=raheem',
    exploreLabel: 'Names Reference — Ar-Raheem',
  },
  ghafur: {
    id: 'ghafur',
    arabic: 'الْغَفُور',
    transliteration: 'Al-Ghafur',
    meaning: 'The Oft-Forgiving',
    root: 'Ghayn - Fa - Ra',
    rootMeanings: ['To forgive', 'To cover', 'To protect from consequences'],
    relatedSequences: [{ id: 's13', title: 'The Waiting Seed', city: 'All Cities' }],
    irlTiles: [{ id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=ghafur',
    exploreLabel: 'Names Reference — Al-Ghafur',
  },
  aziz: {
    id: 'aziz',
    arabic: 'الْعَزِيز',
    transliteration: 'Al-Aziz',
    meaning: 'The All-Mighty',
    root: 'Ayn - Za - Za',
    rootMeanings: ['Mighty', 'Powerful', 'Rare', 'Precious', 'Invincible'],
    relatedSequences: [{ id: 's8', title: 'River Bluff Geology', city: 'Cincinnati' }],
    irlTiles: [{ id: 'fire-hydrant', label: 'Fire Hydrant' }],
    explorePath: '/names-reference?name=aziz',
    exploreLabel: 'Names Reference — Al-Aziz',
  },
  haqq: {
    id: 'haqq',
    arabic: 'الْحَق',
    transliteration: 'Al-Haqq',
    meaning: 'The True Reality',
    root: 'Ha - Qaf - Qaf',
    rootMeanings: ['Truth', 'Right', 'Reality', 'Purpose', 'That which is due'],
    relatedSequences: [{ id: 's14', title: "The Torrent's Foam", city: 'All Cities' }, { id: 's18', title: 'Clay', city: 'All Cities' }],
    irlTiles: [{ id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=haqq',
    exploreLabel: 'Names Reference — Al-Haqq',
  },
  haseeb: {
    id: 'haseeb',
    arabic: 'الْحَسِيب',
    transliteration: 'Al-Haseeb',
    meaning: 'The Reckoner',
    root: 'Ha - Sin - Ba',
    rootMeanings: ['To reckon', 'To take account', 'To be sufficient', 'To count', 'To hold responsible'],
    relatedSequences: [{ id: 's26', title: 'Gravity', city: 'All Cities' }],
    irlTiles: [{ id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=haseeb',
    exploreLabel: 'Names Reference — Al-Haseeb',
  },
  raqib: {
    id: 'raqib',
    arabic: 'الرَّقِيب',
    transliteration: 'Ar-Raqib',
    meaning: 'The Watchful',
    root: 'Ra - Qaf - Ba',
    rootMeanings: ['To watch', 'To observe', 'To guard', 'To be vigilant', 'To keep watch over'],
    relatedSequences: [{ id: 's26', title: 'Gravity', city: 'All Cities' }],
    irlTiles: [{ id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=raqib',
    exploreLabel: 'Names Reference — Ar-Raqib',
  },
  razzaq: {
    id: 'razzaq',
    arabic: 'الرَّزَّاق',
    transliteration: 'Ar-Razzaq',
    meaning: 'The Ever Provider',
    root: 'Ra - Za - Qaf',
    rootMeanings: ['To provide', 'To sustain', 'To give provision', 'To bestow livelihood', 'The One Whose provision is continuous'],
    relatedSequences: [{ id: 's1', title: 'Big Bluestem', city: 'Chicago' }, { id: 's3', title: 'Canopy Photosynthesis', city: 'Atlanta' }, { id: 's6', title: 'Salmon Run', city: 'Portland' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }, { id: 'community-garden', label: 'Community Garden' }],
    explorePath: '/names-reference?name=razzaq',
    exploreLabel: 'Names Reference — Ar-Razzaq',
  },
  musawwir: {
    id: 'musawwir',
    arabic: 'الْمُصَوِّر',
    transliteration: 'Al-Musawwir',
    meaning: 'The Fashioner of Forms',
    root: 'Sad - Waw - Ra',
    rootMeanings: ['To form', 'To fashion', 'To give shape', 'To mold', 'To give unique form to creation'],
    relatedSequences: [{ id: 's5', title: 'Amphibian Metamorphosis', city: 'Detroit' }, { id: 's13', title: 'The Waiting Seed', city: 'All Cities' }],
    irlTiles: [{ id: 'fruit-tree', label: 'Fruit Tree' }],
    explorePath: '/names-reference?name=musawwir',
    exploreLabel: 'Names Reference — Al-Musawwir',
  },
};

// ─── HELPER: get rich NAMES_DATA entry by id ─────────────────────────────────
function getNamesDataEntry(nameId: string): NameEntry | undefined {
  return NAMES_DATA.find(n => n.id === nameId);
}

// ─── SLIDE-OUT PANEL ──────────────────────────────────────────────────────────
interface SlidePanelProps {
  data: NameHighlightData;
  onClose: () => void;
}

function NameSlidePanel({ data, onClose }: SlidePanelProps) {
  const richEntry = getNamesDataEntry(data.id);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(6,13,18,0.6)',
          zIndex: 200,
          backdropFilter: 'blur(2px)',
        }}
      />
      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: 'min(460px, 92vw)',
          background: '#060d12',
          borderLeft: '1px solid rgba(201,168,76,0.25)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInRight 0.32s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}</style>

        {/* Panel Header */}
        <div style={{
          padding: '20px 20px 16px',
          borderBottom: '1px solid rgba(201,168,76,0.15)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexShrink: 0,
          background: 'rgba(201,168,76,0.03)',
        }}>
          <div>
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 32, color: '#c9a84c', lineHeight: 1.2, marginBottom: 4 }}>
              {data.arabic}
            </div>
            <div style={{ fontSize: 15, color: '#d4c5a9', fontWeight: 600 }}>{data.transliteration}</div>
            <div style={{ fontSize: 12, color: '#9a8f7d', marginTop: 3 }}>{data.meaning}</div>
            {richEntry?.category && (
              <div style={{ marginTop: 6 }}>
                <span style={{
                  fontSize: 9,
                  color: '#2d8a87',
                  background: 'rgba(45,138,135,0.1)',
                  border: '1px solid rgba(45,138,135,0.2)',
                  borderRadius: 3,
                  padding: '2px 8px',
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                }}>{richEntry.category}</span>
                {richEntry.isAnchor && (
                  <span style={{
                    marginLeft: 6,
                    fontSize: 9,
                    color: '#c9a84c',
                    background: 'rgba(201,168,76,0.1)',
                    border: '1px solid rgba(201,168,76,0.25)',
                    borderRadius: 3,
                    padding: '2px 8px',
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                  }}>Anchor Name</span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid rgba(201,168,76,0.2)',
              color: 'rgba(201,168,76,0.6)',
              cursor: 'pointer',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              flexShrink: 0,
              marginLeft: 12,
            }}
            aria-label="Close panel"
          >
            ×
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* ── Quranic Root ── */}
          <div>
            <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
              QURANIC ROOT
            </div>
            <div style={{ fontSize: 12, color: '#c9a84c', marginBottom: 6, fontFamily: "'Amiri', serif" }}>{data.root}</div>
            {richEntry?.rootMeaning && (
              <div style={{ fontSize: 11, color: '#7a6f5d', marginBottom: 8, fontStyle: 'italic' }}>{richEntry.rootMeaning}</div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {data.rootMeanings.map((m, i) => (
                <span key={i} style={{
                  fontSize: 10,
                  color: '#9a8f7d',
                  background: 'rgba(201,168,76,0.05)',
                  border: '1px solid rgba(201,168,76,0.12)',
                  borderRadius: 3,
                  padding: '3px 8px',
                }}>{m}</span>
              ))}
            </div>
          </div>

          {/* ── Quranic References (from NAMES_DATA) ── */}
          {richEntry?.quranicRefs && richEntry.quranicRefs.length > 0 && (
            <div>
              <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
                QURANIC REFERENCES
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {richEntry.quranicRefs.slice(0, 3).map((qr, i) => (
                  <div key={i} style={{
                    padding: '10px 12px',
                    background: 'rgba(201,168,76,0.04)',
                    border: '1px solid rgba(201,168,76,0.12)',
                    borderRadius: 4,
                  }}>
                    <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.08em', marginBottom: 4 }}>{qr.ref}</div>
                    <div style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.6, fontStyle: 'italic' }}>{qr.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Letter Source ── */}
          {richEntry?.letterSource && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(45,138,135,0.04)',
              border: '1px solid rgba(45,138,135,0.15)',
              borderRadius: 4,
            }}>
              <div style={{ fontSize: 8, color: '#2d8a87', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 4 }}>
                SOURCE IN OPEN LETTER
              </div>
              <div style={{ fontSize: 11, color: '#9a8f7d' }}>{richEntry.letterSource}</div>
            </div>
          )}

          {/* ── Key Connections (from NAMES_DATA) ── */}
          {richEntry?.keyConnections && richEntry.keyConnections.length > 0 && (
            <div>
              <div style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
                KEY CONNECTIONS
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {richEntry.keyConnections.map((kc, i) => (
                  <div key={i} style={{
                    padding: '10px 12px',
                    background: 'rgba(45,122,71,0.05)',
                    border: '1px solid rgba(45,122,71,0.18)',
                    borderRadius: 4,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{
                        fontSize: 8,
                        color: '#2d7a47',
                        background: 'rgba(45,122,71,0.12)',
                        border: '1px solid rgba(45,122,71,0.25)',
                        borderRadius: 2,
                        padding: '2px 6px',
                        letterSpacing: '.08em',
                      }}>KEY {kc.keyNum}</span>
                      <span style={{ fontSize: 9, color: '#2d7a47', letterSpacing: '.06em' }}>{kc.keyId}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.6 }}>{kc.connection}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Related Sequences ── */}
          {data.relatedSequences.length > 0 && (
            <div>
              <div style={{ fontSize: 8, color: '#2d8a87', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
                RELATED SEQUENCES
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.relatedSequences.map((s, i) => {
                  // Try to get richer sequence info from NAMES_DATA
                  const richSeq = richEntry?.sequences?.find(rs => rs.seqId === s.id);
                  return (
                    <div key={i} style={{
                      padding: '8px 12px',
                      background: 'rgba(45,138,135,0.06)',
                      border: '1px solid rgba(45,138,135,0.15)',
                      borderRadius: 4,
                    }}>
                      <div style={{ fontSize: 11, color: '#2d8a87' }}>{s.title}</div>
                      <div style={{ fontSize: 9, color: '#7a6f5d', marginTop: 2 }}>{s.city}</div>
                      {richSeq?.role && (
                        <div style={{ fontSize: 10, color: '#9a8f7d', marginTop: 4, lineHeight: 1.5, fontStyle: 'italic' }}>
                          {richSeq.role}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── IRL Tiles ── */}
          {data.irlTiles.length > 0 && (
            <div>
              <div style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
                IRL TILES
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {data.irlTiles.map((t, i) => (
                  <span key={i} style={{
                    fontSize: 10,
                    color: '#2d7a47',
                    background: 'rgba(45,122,71,0.08)',
                    border: '1px solid rgba(45,122,71,0.2)',
                    borderRadius: 3,
                    padding: '4px 10px',
                  }}>{t.label}</span>
                ))}
              </div>
            </div>
          )}

          {/* ── Cross-Name Connections (from NAMES_DATA) ── */}
          {richEntry?.crossNameConnections && richEntry.crossNameConnections.length > 0 && (
            <div>
              <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
                PAIRED & RELATED NAMES
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {richEntry.crossNameConnections.map((cn, i) => {
                  const crossEntry = NAMES_DATA.find(n => n.id === cn);
                  return (
                    <span key={i} style={{
                      fontSize: 10,
                      color: '#c9a84c',
                      background: 'rgba(201,168,76,0.07)',
                      border: '1px solid rgba(201,168,76,0.2)',
                      borderRadius: 3,
                      padding: '4px 10px',
                    }}>
                      {crossEntry ? crossEntry.transliteration : cn}
                    </span>
                  );
                })}
              </div>
              {richEntry.pairedWith && (
                <div style={{ marginTop: 8, fontSize: 10, color: '#7a6f5d' }}>
                  Paired with: <span style={{ color: '#c9a84c' }}>
                    {NAMES_DATA.find(n => n.id === richEntry.pairedWith)?.transliteration ?? richEntry.pairedWith}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Field Prompt (from NAMES_DATA) ── */}
          {richEntry?.fieldPrompt && (
            <div style={{
              padding: '14px 16px',
              background: 'rgba(45,138,135,0.05)',
              border: '1px solid rgba(45,138,135,0.18)',
              borderRadius: 6,
            }}>
              <div style={{ fontSize: 8, color: '#2d8a87', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                ✦ CARETAKER FIELD PROMPT
              </div>
              <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>
                {richEntry.fieldPrompt}
              </p>
            </div>
          )}

          {/* Divider before explore */}
          <div style={{ height: 1, background: 'rgba(201,168,76,0.1)' }} />

          {/* Explore Further CTA */}
          <div style={{
            background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: 6,
            padding: '16px',
          }}>
            <div style={{ fontSize: 9, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
              ⚠ Explore Further
            </div>
            <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.6, marginBottom: 12 }}>
              The following link will take you to the full reference page for this Name — this will interrupt your reading of the letter. You can return here at any time.
            </p>
            {data.explorePath && (
              <Link
                href={data.explorePath}
                style={{
                  display: 'block',
                  textAlign: 'center',
                  padding: '10px 16px',
                  background: 'rgba(201,168,76,0.1)',
                  border: '1px solid rgba(201,168,76,0.3)',
                  color: '#c9a84c',
                  fontSize: 11,
                  letterSpacing: '.08em',
                  textDecoration: 'none',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.18)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.1)'; }}
              >
                → {data.exploreLabel || 'Explore in Platform'}
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

interface NameHighlightProps {
  nameId: string;
  displayText: string;
  /** If true, clicking opens slide panel. Default: true */
  navigateOnClick?: boolean;
}

export default function NameHighlight({ nameId, displayText, navigateOnClick = true }: NameHighlightProps) {
  const [open, setOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [pos, setPos] = useState<'above' | 'below'>('above');
  const ref = useRef<HTMLSpanElement>(null);
  const data = NAME_HIGHLIGHT_DATA[nameId];
  const richEntry = getNamesDataEntry(nameId);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos(rect.top > 200 ? 'above' : 'below');
    }
    setOpen(true);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigateOnClick && data) {
      setPanelOpen(true);
      setOpen(false);
    }
  };

  if (!data) {
    return <span style={{ color: '#c9a84c', borderBottom: '1px dotted rgba(201,168,76,0.5)' }}>{displayText}</span>;
  }

  return (
    <>
      <span
        ref={ref}
        style={{ position: 'relative', display: 'inline' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setOpen(false)}
      >
        <span
          onClick={handleClick}
          style={{
            color: '#c9a84c',
            borderBottom: open ? '1px solid rgba(201,168,76,0.8)' : '1px dotted rgba(201,168,76,0.5)',
            cursor: 'pointer',
            background: open ? 'rgba(201,168,76,0.08)' : 'transparent',
            transition: 'all 0.2s',
            padding: '0 1px',
          }}>
          {displayText}
        </span>

        {open && (
          <span style={{
            position: 'absolute',
            [pos === 'above' ? 'bottom' : 'top']: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(6,13,18,0.97)',
            border: '1px solid rgba(201,168,76,0.3)',
            borderRadius: 6,
            padding: '14px 16px',
            width: 280,
            zIndex: 50,
            boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
            pointerEvents: 'none',
            display: 'block',
          }}>
            {/* Arabic + transliteration */}
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
              <span style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: '#c9a84c' }}>{data.arabic}</span>
              <span style={{ fontSize: 11, color: '#d4c5a9' }}>{data.transliteration}</span>
            </span>
            {/* Meaning */}
            <span style={{ display: 'block', fontSize: 13, color: '#d4c5a9', marginBottom: 6 }}>{data.meaning}</span>
            {/* Root */}
            <span style={{ display: 'block', fontSize: 9, color: '#7a6f5d', letterSpacing: '.08em', marginBottom: 4 }}>
              QURANIC ROOT: {data.root}
            </span>
            {/* Root meanings */}
            <span style={{ display: 'block', fontSize: 10, color: '#9a8f7d', lineHeight: 1.5, marginBottom: 8 }}>
              {data.rootMeanings.slice(0, 3).join(' · ')}
            </span>
            {/* First Quranic ref from NAMES_DATA if available */}
            {richEntry?.quranicRefs?.[0] && (
              <span style={{ display: 'block', marginBottom: 8 }}>
                <span style={{ display: 'block', fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 3 }}>
                  {richEntry.quranicRefs[0].ref}
                </span>
                <span style={{ display: 'block', fontSize: 10, color: '#9a8f7d', lineHeight: 1.5, fontStyle: 'italic' }}>
                  {richEntry.quranicRefs[0].text.length > 80
                    ? richEntry.quranicRefs[0].text.slice(0, 80) + '…'
                    : richEntry.quranicRefs[0].text}
                </span>
              </span>
            )}
            {/* Related sequences */}
            {data.relatedSequences.length > 0 && (
              <span style={{ display: 'block', marginBottom: 6 }}>
                <span style={{ display: 'block', fontSize: 8, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>RELATED SEQUENCES:</span>
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {data.relatedSequences.slice(0, 3).map((s, i) => (
                    <span key={i} style={{
                      fontSize: 9,
                      color: '#2d8a87',
                      background: 'rgba(45,138,135,0.1)',
                      border: '1px solid rgba(45,138,135,0.2)',
                      borderRadius: 3,
                      padding: '2px 6px',
                    }}>{s.title}</span>
                  ))}
                </span>
              </span>
            )}
            {/* IRL Tiles */}
            {data.irlTiles.length > 0 && (
              <span style={{ display: 'block', marginBottom: 8 }}>
                <span style={{ display: 'block', fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>IRL TILES:</span>
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {data.irlTiles.slice(0, 3).map((t, i) => (
                    <span key={i} style={{
                      fontSize: 9,
                      color: '#2d7a47',
                      background: 'rgba(45,122,71,0.1)',
                      border: '1px solid rgba(45,122,71,0.2)',
                      borderRadius: 3,
                      padding: '2px 6px',
                    }}>{t.label}</span>
                  ))}
                </span>
              </span>
            )}
            {/* Click hint */}
            <span style={{ display: 'block', fontSize: 9, color: '#c9a84c', borderTop: '1px solid rgba(201,168,76,0.1)', paddingTop: 6, marginTop: 4 }}>
              ↗ Click to open full reference panel
            </span>
          </span>
        )}
      </span>

      {/* Slide-out panel — rendered at root level via portal-like pattern */}
      {panelOpen && (
        <NameSlidePanel data={data} onClose={() => setPanelOpen(false)} />
      )}
    </>
  );
}
